/**
 * Permission Cache Implementation
 * 
 * High-performance caching layer for permission results to avoid
 * repeated expensive calculations and database queries.
 */

import {
  PermissionResult,
  PermissionCacheEntry,
  IPermissionCache,
  PERMISSION_CONFIG
} from '../types';

/**
 * In-memory permission cache with TTL and size limits
 */
export class PermissionCache implements IPermissionCache {
  private cache = new Map<string, PermissionCacheEntry>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Start periodic cleanup
    this.startCleanup();
  }

  /**
   * Get cached permission result
   */
  get(key: string): PermissionCacheEntry | null {
    const entry = this.cache.get(key);
    
    if (!entry) return null;

    // Check if entry has expired
    if (Date.now() > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry;
  }

  /**
   * Set permission result in cache
   */
  set(key: string, result: PermissionResult, ttl?: number): void {
    // Use default TTL if not provided
    const actualTTL = ttl || PERMISSION_CONFIG.CACHE_TTL_DEFAULT;

    const entry: PermissionCacheEntry = {
      key,
      result,
      timestamp: Date.now(),
      ttl: actualTTL
    };

    // Check cache size limit
    if (this.cache.size >= PERMISSION_CONFIG.MAX_CACHE_SIZE) {
      this.evictOldest();
    }

    this.cache.set(key, entry);
  }

  /**
   * Clear cache entries matching a pattern
   */
  clear(pattern?: string): void {
    if (!pattern) {
      // Clear all entries
      this.cache.clear();
      return;
    }

    // Clear entries matching pattern
    const regex = new RegExp(pattern.replace(/\*/g, '.*'));
    const keysToDelete: string[] = [];

    for (const [key] of this.cache.entries()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache entries for a specific user
   */
  clearUser(userId: string): void {
    this.clear(`user:${userId}*`);
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    hitRate: number;
    memoryUsage: number;
  } {
    // This is a simplified implementation
    // In a real application, you might want to track hit/miss ratios
    return {
      size: this.cache.size,
      hitRate: 0, // Would need to implement hit/miss tracking
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Start periodic cleanup of expired entries
   */
  private startCleanup(): void {
    // Clean up expired entries every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, 5 * 60 * 1000);
  }

  /**
   * Stop periodic cleanup (useful for testing or shutdown)
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Remove expired entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.timestamp + entry.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));

    if (keysToDelete.length > 0) {
      // Permission cache: cleaned up expired entries
    }
  }

  /**
   * Evict oldest entries when cache is full
   */
  private evictOldest(): void {
    // Find the oldest entry
    let oldestKey: string | null = null;
    let oldestTimestamp = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Estimate memory usage (simplified)
   */
  private estimateMemoryUsage(): number {
    // Rough estimate: each entry is approximately 200 bytes
    // This is a simplified calculation
    return this.cache.size * 200;
  }

  /**
   * Export cache contents (useful for debugging)
   */
  exportCache(): PermissionCacheEntry[] {
    return Array.from(this.cache.values());
  }

  /**
   * Import cache contents (useful for testing)
   */
  importCache(entries: PermissionCacheEntry[]): void {
    this.cache.clear();
    entries.forEach(entry => {
      this.cache.set(entry.key, entry);
    });
  }
}

/**
 * Smart cache key generators for different scenarios
 */
export class CacheKeyGenerator {
  static userRole(userId: string, companyId?: string): string {
    return `role:${userId}:${companyId || 'none'}`;
  }

  static projectMembership(userId: string, projectId: string): string {
    return `project:${userId}:${projectId}`;
  }

  static companyContracts(companyId: string): string {
    return `contracts:${companyId}`;
  }

  static permissionCheck(
    userId: string,
    action: string,
    resource: string,
    resourceId?: string,
    companyScope?: string
  ): string {
    return [
      `perm:${userId}`,
      `${action}:${resource}`,
      resourceId ? `id:${resourceId}` : 'id:none',
      companyScope ? `scope:${companyScope}` : 'scope:none'
    ].join('|');
  }
}

/**
 * Cache warming utilities
 */
export class CacheWarmer {
  private _cache: PermissionCache;

  constructor(cache: PermissionCache) {
    this._cache = cache;
  }

  /**
   * Pre-warm cache with common permission checks for a user
   */
  async warmUserCache(_userId: string, _commonChecks: Array<{
    action: string;
    resource: string;
    resourceId?: string;
  }>): Promise<void> {
    // This would typically make the actual permission checks
    // to populate the cache with commonly used permissions
    // Warming cache for user with common permission checks

    // Implementation would depend on having access to the permission service
    // For now, this is a placeholder - this._cache would be used here
    // The _cache instance would be used in a real implementation
    this._cache; // Reference to satisfy TypeScript (will be used in real implementation)
  }

  /**
   * Pre-warm cache for a new user session
   */
  async warmSessionCache(userId: string, userRole: string, _companyId?: string): Promise<void> {
    const commonChecks = this.getCommonChecksForRole(userRole);
    await this.warmUserCache(userId, commonChecks);
  }

  private getCommonChecksForRole(role: string): Array<{
    action: string;
    resource: string;
    resourceId?: string;
  }> {
    // Define common permission checks based on role
    switch (role) {
      case 'ADMIN':
        return [
          { action: 'view', resource: 'company' },
          { action: 'view', resource: 'user' },
          { action: 'create', resource: 'company' },
          { action: 'create', resource: 'user' }
        ];

      case 'COMPANY_ADMIN':
        return [
          { action: 'view', resource: 'company' },
          { action: 'view', resource: 'user' },
          { action: 'view', resource: 'project' },
          { action: 'create', resource: 'user' },
          { action: 'create', resource: 'project' }
        ];

      case 'USER':
        return [
          { action: 'view', resource: 'project' },
          { action: 'view', resource: 'user' }
        ];

      default:
        return [];
    }
  }
}